/*
 * Contact details as a person would write them.
 *
 * Phone numbers are stored in the E.164 form the phone field produces
 * (+447700900902), which is right for dialling and wrong for reading. These
 * helpers are display-only: the stored value is what we still put in the
 * tel: link, so nothing depends on the formatting being perfect.
 */

/** Digits and a leading +, nothing else. */
function clean(raw) {
  const s = String(raw || '').trim();
  if (!s) return '';
  const plus = s.startsWith('+');
  const digits = s.replace(/\D/g, '');
  return plus ? `+${digits}` : digits;
}

/**
 * A UK number in the form people write it: +447700900902 -> "07700 900902".
 * Anything that is not recognisably a UK number is returned unchanged, so a
 * number we cannot parse is shown as stored rather than mangled.
 *
 * @param {string} raw - the stored number
 * @returns {string} the number to display
 */
export function formatUkPhone(raw) {
  const value = clean(raw);
  if (!value) return '';

  // +44 and 0044 are both the same national number written long.
  let national = value;
  if (national.startsWith('+44')) national = `0${national.slice(3)}`;
  else if (national.startsWith('0044')) national = `0${national.slice(4)}`;

  if (!/^0\d+$/.test(national)) return String(raw || '').trim();

  // Mobiles and most geographic numbers: five then six.
  if (/^07\d{9}$/.test(national)) return `${national.slice(0, 5)} ${national.slice(5)}`;
  // Two-digit area codes (020 London, 0161 Manchester and the rest of the
  // 02x range): three, then four, then four.
  if (/^02\d{9}$/.test(national)) return `${national.slice(0, 3)} ${national.slice(3, 7)} ${national.slice(7)}`;
  if (/^0\d{10}$/.test(national)) return `${national.slice(0, 5)} ${national.slice(5)}`;

  return national;
}

/**
 * The href for a phone link. Always the stored value, never the formatted
 * one, so the dialled number is the number we hold.
 *
 * @param {string} raw - the stored number
 * @returns {string} a tel: URL
 */
export function telHref(raw) {
  return `tel:${clean(raw)}`;
}
